import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { isGtmAllowedHost } from "@/lib/datalayer";
import { persistClickIdsIfConsented } from "@/lib/utm";
import { useRouterState } from "@tanstack/react-router";
import { ConsentContext, type ConsentContextValue } from "./ConsentContext";
import { loadConsent, purgeTrackingCookies, saveConsent } from "@/lib/consent/storage";
import {
  ALL_GRANTED,
  DEFAULT_CATEGORIES,
  type ConsentCategories,
  type ConsentState,
} from "@/lib/consent/types";
import { isPrivateRoute } from "@/lib/consent/private-routes";

const GA4_ID = (import.meta.env.VITE_GA4_MEASUREMENT_ID as string | undefined) ?? "";
const ADS_ID = (import.meta.env.VITE_GOOGLE_ADS_ID as string | undefined) ?? "";
const MOUSEFLOW_ID = (import.meta.env.VITE_MOUSEFLOW_ID as string | undefined) ?? "";

type GtagFn = (...args: unknown[]) => void;
declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: GtagFn;
    _mfq?: unknown[];
    oaiq?: OaiqFn;
  }
}

type OaiqFn = ((...args: unknown[]) => void) & { q?: unknown[] };

function ensureGtag(): GtagFn {
  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer!.push(arguments);
    } as GtagFn;
  }
  return window.gtag!;
}

function setConsentModeDefault() {
  // Consent-Mode-Defaults werden bereits inline im <head> gesetzt
  // (vor dem GTM-Loader). Nur sicherstellen, dass gtag verfügbar ist.
  ensureGtag();
}

function updateConsentMode(c: ConsentCategories) {
  const gtag = ensureGtag();
  gtag("consent", "update", {
    ad_storage: c.marketing ? "granted" : "denied",
    ad_user_data: c.marketing ? "granted" : "denied",
    ad_personalization: c.marketing ? "granted" : "denied",
    analytics_storage: c.statistics ? "granted" : "denied",
  });
}

function injectScript(id: string, src: string) {
  if (document.getElementById(id)) return;
  const s = document.createElement("script");
  s.id = id;
  s.async = true;
  s.src = src;
  document.head.appendChild(s);
}

const GTM_ID = "GTM-WNPVG62";
const IMMEDIATE_GTM_PATHS = ["/termin/danke", "/termin/danke-email", "/mentoring/danke"];

/**
 * Führt `fn` erst aus, wenn der Browser Leerlauf hat (oder nach Timeout bzw.
 * bei der ersten Nutzerinteraktion). Hält GTM vom kritischen Renderpfad fern.
 */
function runWhenIdle(fn: () => void) {
  if (typeof window === "undefined") return;
  let done = false;
  const run = () => {
    if (done) return;
    done = true;
    window.removeEventListener("scroll", run);
    window.removeEventListener("pointerdown", run);
    window.removeEventListener("keydown", run);
    fn();
  };
  window.addEventListener("scroll", run, { once: true, passive: true });
  window.addEventListener("pointerdown", run, { once: true });
  window.addEventListener("keydown", run, { once: true });
  const ric = (window as unknown as {
    requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
  }).requestIdleCallback;
  if (typeof ric === "function") ric(run, { timeout: 4000 });
  else window.setTimeout(run, 2500);
}

function loadGtm() {
  if (document.getElementById("gtm-container")) return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });
  injectScript("gtm-container", `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`);
}

function loadGoogleTags() {
  const tagId = GA4_ID || ADS_ID;
  if (!tagId) return;
  injectScript("gtag-base", `https://www.googletagmanager.com/gtag/js?id=${tagId}`);
  const gtag = ensureGtag();
  gtag("js", new Date());
  if (GA4_ID) gtag("config", GA4_ID, { anonymize_ip: true });
  if (ADS_ID) gtag("config", ADS_ID);
}


const CHATGPT_PIXEL_ID = "UjnhXNudvTR1uZLtqSyrRd";

/**
 * ChatGPT-Ads-Pixel (OpenAI). Lädt ausschließlich nach erteilter
 * Marketing-Einwilligung; ohne Einwilligung wird nichts an OpenAI
 * übertragen. Doppellade-Schutz über die Skript-ID.
 */
function loadChatGptPixel() {
  if (document.getElementById("chatgpt-pixel")) return;
  if (!window.oaiq) {
    const q = function (this: unknown, ...args: unknown[]) {
      (q.q as unknown[]).push(args);
    } as OaiqFn;
    q.q = [];
    window.oaiq = q;
  }
  injectScript("chatgpt-pixel", "https://bzrcdn.openai.com/sdk/oaiq.min.js");
  // init muss vor bereits eingereihten Ereignissen (z. B. Danke-Seite) stehen.
  const initArgs = ["init", { pixelId: CHATGPT_PIXEL_ID }];
  if (Array.isArray(window.oaiq.q)) window.oaiq.q.unshift(initArgs);
  else window.oaiq("init", { pixelId: CHATGPT_PIXEL_ID });
}

function loadMouseflow() {
  if (!MOUSEFLOW_ID) return;
  if (document.getElementById("mouseflow-script")) return;
  window._mfq = window._mfq || [];
  injectScript("mouseflow-script", `https://cdn.mouseflow.com/projects/${MOUSEFLOW_ID}.js`);
}

function stopMouseflow() {
  if (typeof window === "undefined") return;
  window._mfq = window._mfq || [];
  try {
    window._mfq.push(["stop"]);
  } catch {
    /* ignore */
  }
}

export function ConsentProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ConsentState | null>(null);
  const [ready, setReady] = useState(false);
  const [isSettingsOpen, setSettingsOpen] = useState(false);
  const defaultsSetRef = useRef(false);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isPrivate = isPrivateRoute(pathname);

  // Consent-Mode-Defaults SOFORT setzen (vor jedem Tag-Load),
  // dann gespeicherten Consent lesen.
  useEffect(() => {
    if (!defaultsSetRef.current) {
      defaultsSetRef.current = true;
      setConsentModeDefault();
    }
    const loaded = loadConsent();
    setState(loaded);
    setReady(true);
  }, []);

  // Scripts gemäß aktuellem Consent + Route (de)aktivieren.
  useEffect(() => {
    if (!state) return;
    updateConsentMode(state.categories);
    if (state.categories.marketing) persistClickIdsIfConsented();
    if (
      (state.categories.statistics || state.categories.marketing) &&
      isGtmAllowedHost(window.location.hostname)
    ) {
      const loadTags = () => {
        loadGtm();
        loadGoogleTags();
      };
      // Ticket TR1: Auf Danke-Seiten sofort laden, damit das Ereignis nicht
      // verloren geht, wenn der Besuch schnell endet.
      if (IMMEDIATE_GTM_PATHS.includes(pathname)) loadTags();
      else runWhenIdle(loadTags);
    }

    // ChatGPT-Ads-Pixel: nur nach Marketing-Einwilligung, auf allen
    // Routen (Conversions zählen auch auf Danke-/Formularseiten).
    if (state.categories.marketing) {
      loadChatGptPixel();
    }

    // Mouseflow läuft ausschließlich auf öffentlichen Seiten und
    // nur bei erteiltem Statistik-Consent.
    if (state.categories.statistics && !isPrivate) {
      loadMouseflow();
    } else {
      stopMouseflow();
    }
    purgeTrackingCookies({
      statistics: !state.categories.statistics,
      marketing: !state.categories.marketing,
    });
  }, [state, isPrivate, pathname]);

  const acceptAll = useCallback(() => {
    setState(saveConsent(ALL_GRANTED));
    setSettingsOpen(false);
  }, []);

  const rejectAll = useCallback(() => {
    setState(saveConsent(DEFAULT_CATEGORIES));
    setSettingsOpen(false);
  }, []);

  const saveCustom = useCallback((c: ConsentCategories) => {
    setState(saveConsent(c));
    setSettingsOpen(false);
  }, []);

  const openSettings = useCallback(() => setSettingsOpen(true), []);
  const closeSettings = useCallback(() => setSettingsOpen(false), []);

  const value = useMemo<ConsentContextValue>(
    () => ({
      state,
      ready,
      acceptAll,
      rejectAll,
      saveCustom,
      openSettings,
      closeSettings,
      isSettingsOpen,
    }),
    [state, ready, acceptAll, rejectAll, saveCustom, openSettings, closeSettings, isSettingsOpen],
  );

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
}
